import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
    useRef,
    useCallback,
} from 'react';

import { useBalloonEventsOptions } from './EmbedOptions';
import {
    BalloonCluster,
    BalloonEventModel,
    BeatAPISearchV3Repository,
    GlobalBoundingBox,
} from 'ts-core';

import {
    useApi,
    useQuery,
    useBalloonEventsMetadata,
    BalloonEventNotifier,
} from 'react-ui';
import { useIsMobile } from "react-ui";
import { useNavigate, useLocation } from 'react-router-dom';
import { EXCLUDED_PROVIDERS } from '../hooks/useMapHotspots';

const EventDataContext = createContext<EventDataInfo | undefined>(undefined);

// Default page sizes for the sidebar/list. Restored from main: small batches
// keep the list readable and the network light. The clustering path needs many
// more events so supercluster has density to work with — and a saved search
// can override either default explicitly via filters.amount.
const DEFAULT_AMOUNT_DESKTOP = 20;
const DEFAULT_AMOUNT_MOBILE = 15;
// Bumped from 300 → 2000 (and mobile 150 → 1000): saved-search clustering paths
// like langenachtderkirchen can carry ~2000 balloons; supercluster needs the full
// set in the cache to render a single accurate count at the most-zoomed-out level
// rather than a fraction.
const CLUSTERING_AMOUNT_DESKTOP = 2000;
const CLUSTERING_AMOUNT_MOBILE = 1000;

function shallowArrayEqual<T>(a: readonly T[], b: readonly T[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

function resolveAmount(
    query: { amount?: number; forceMapClustering?: boolean },
    isMobile: boolean
): number {
    // Explicit override (e.g. saved search supplied filters.amount, or callers
    // that pass a specific amount) always wins.
    if (typeof query.amount === 'number' && query.amount > 0) {
        return query.amount;
    }
    if (query.forceMapClustering) {
        return isMobile ? CLUSTERING_AMOUNT_MOBILE : CLUSTERING_AMOUNT_DESKTOP;
    }
    return isMobile ? DEFAULT_AMOUNT_MOBILE : DEFAULT_AMOUNT_DESKTOP;
}

interface EventDataInfo {
    events: BalloonEventModel[];
    /**
     * Cluster items returned by /api/v3/search when ENABLE_CLUSTERING=true on
     * the backend AND map zoom <= CLUSTERING_MAX_ZOOM. Empty array otherwise.
     * Front-ends MUST treat an empty array as "no clustering" — no special
     * handling required, balloons render as before.
     */
    clusters: BalloonCluster[];
    /**
     * Viewport-independent bounding box of the whole filtered set, returned by
     * the backend only when the last search opted in via include_global_bbox=1
     * (armed through `pendingAutoFit` when a tag/category/user/partner route is
     * entered). null otherwise. BalloonEventsMapBox reads this to fit the map
     * once, then disarms the signal.
     */
    globalBoundingBox: GlobalBoundingBox | null;
    selectedEvent: BalloonEventModel | null;
    selectEvent: (id: string, x?: number, y?: number, isMobile?: boolean) => void;
    deselectEvent: () => void;
    allFilteredEvents: BalloonEventModel[];
    ensureLoad: (id: string) => Promise<BalloonEventModel | undefined>;
    loading: boolean;
    getEvent: (id: string) => BalloonEventModel | undefined;
    eventMap: Map<string, BalloonEventModel>;
    hoveredEventId: string;
    setHoveredEventId: (id: string) => void;
    openedEvent: BalloonEventModel | null;
    setOpenedEvent: (event: BalloonEventModel | null) => void;
    /** Refresh events by re-running the current query */
    refreshEvents: () => Promise<void>;
    openEvent: (id: string) => void;
}

type BalloonEventsProviderProps = {
    children: ReactNode;
};

export const EventDataProvider: React.FC<BalloonEventsProviderProps> = ({
    children,
}) => {
    const { apiEndpoints, apiKey } = useApi();
    const { metadata: metaData } = useBalloonEventsMetadata();
    const [events, setEvents] = useState<BalloonEventModel[]>([]);
    const [hoveredEventId, setHoveredEventId] = useState('');
    const [openedEvent, setOpenedEvent] = useState<BalloonEventModel | null>(null);
    const [allFilteredEvents, setAllFilteredEvents] = useState<
        BalloonEventModel[]
    >([]);
    const [selectedEvent, setSelectedEvent] =
        useState<BalloonEventModel | null>(null);
    const {
        balloonIds,
        balloonData,
        loadFromQuery,
        selectRandomEvent,
        showDebug,
        key,
        initialSelectedEventId,
        selectedBalloon,
        routingType,
    } = useBalloonEventsOptions();

    // URL writes branch on routing type — they ARE NOT the same operation:
    //
    //  - HASH mode (embed): `navigate()` writes the hash fragment. The host
    //    page's react-router (if any) doesn't watch the hash, so this can't
    //    leak into the host's routing. RouterChecker has installed a
    //    HashRouter, so `navigate()` here writes `#/event/<slug>`.
    //
    //  - BROWSER mode (site/'s map page): the map shares the BrowserRouter
    //    with the host app (site/'s App.tsx). Calling `navigate('/event/X')`
    //    propagates to site/'s top-level router which HAS a matching
    //    `/event/:slugOrId` Route → EventPage. That unmounts the entire map
    //    and replaces it with the landing-page route. The user reported this
    //    exact regression: clicking a tile jumped them to the landing page
    //    instead of sliding the details pane in.
    //
    //    The contract the user wants is: URL reflects `/event/<slug>` so it's
    //    shareable, the panel slides in (state-driven, no route involved),
    //    AND a fresh page load or shared link at `/event/<slug>` still hits
    //    site/'s EventPage. `window.history.replaceState` is exactly that —
    //    it updates the URL bar without notifying react-router, so site/'s
    //    Router stays on whatever route mounted the map (`/`) and never
    //    re-renders. The panel renders from `openedEvent` state via
    //    `BalloonDetails → EventDetailPage` in BalloonEventsUI.
    const navigate = useNavigate();
    const location = useLocation();

    const { query } = useQuery();
    const isMobile = useIsMobile();

    const [callcount, setCallcount] = useState(0);

    const [clusters, setClusters] = useState<BalloonCluster[]>([]);
    const [globalBoundingBox, setGlobalBoundingBox] = useState<GlobalBoundingBox | null>(null);

    const repositoryRef = useRef<BeatAPISearchV3Repository>(
        new BeatAPISearchV3Repository(
            metaData,
            apiEndpoints,
            apiKey || '',
            showDebug,
            [],
            EXCLUDED_PROVIDERS,
            key
        )
    );

    const initialSelectDoneRef = useRef(false);

    const [injected, setInjected] = useState<BalloonEventModel[]>([]);

    useEffect(() => {
        if (!balloonData) return;
        repositoryRef.current.inject(balloonData);
        setInjected(balloonData);
    }, [balloonData]);

    useEffect(() => {
        if (!balloonIds) return;
        loadAll(balloonIds);
        repositoryRef.current.getAll(balloonIds).then((events) => {
            setInjected(events.filter((e): e is BalloonEventModel => e !== undefined));
        });
    }, [balloonIds]);

    const selectEvent = useCallback(async (id: string, x?: number, y?: number, isMobile?: boolean) => {
        console.log('++++++++++++++++++++++selecting event', id, 'isMobile:', isMobile);
        if (hoveredEventId !== id) {
            setHoveredEventId(id);
        }
        const event = repositoryRef.current.get(id);
        console.log('++++++++++++++++++++++selecting event object', event);
        if (x && y) {
            event?.setScreenCoordinates(x, y)
        }
        if (!event) {
            console.log('failed to select');
            return;
        }

        const isOpened = openedEvent?.id === event.id;
        const isSelected = selectedEvent?.id === event.id;

        // Pure state machine. URL writes happen in the openedEvent→URL effect
        // below — keeping click handlers and URL imperatives decoupled fixes
        // the 3-click bug (URL races with state) and the close-doesn't-clear-
        // hash bug (deselectEvent / banner overlays previously bypassed the
        // URL writes entirely).
        if (isOpened) {
            setOpenedEvent(null);
            return;
        }
        if (isSelected) {
            setOpenedEvent(event);
            return;
        }
        // On mobile: first tap selects, carousel scrolls to show it.
        // On desktop: hover already "selected" visually, click confirms selection.
        setSelectedEvent(event);
    }, [selectedEvent, openedEvent, setOpenedEvent, hoveredEventId]);

    // Single source of truth for the event-detail URL: react to `openedEvent`
    // and `navigate()` accordingly. Works for every code path that mutates
    // `openedEvent` (selectEvent, deselectEvent, BalloonEventNotifier,
    // BannerContainerV2, ZeppelinContainerV2, …) without each having to
    // remember to update the URL.
    //
    // The `hasOpenedRef` guard preserves deep links: if the user lands on
    // `/event/<slug>` with `openedEvent === null` (the initial-select effect
    // only sets `selectedEvent`, not `openedEvent`), we must NOT navigate to
    // `/` and wipe their URL. We only emit a "close" navigation when
    // `openedEvent` has previously been non-null in this session.
    const hasOpenedRef = useRef(false);
    useEffect(() => {
        // In browser mode (site/) we bypass react-router and write the URL
        // directly. That means `location.pathname` from `useLocation()` does
        // NOT reflect the real URL bar after we've written — it stays at
        // whatever react-router was last told about (usually `/`). So we
        // compare against `window.location.pathname` instead, which is the
        // source of truth for what's actually shown to the user. Hash mode
        // still uses react-router's location because there `navigate()` and
        // react-router's `location` are kept in sync by HashRouter.
        const writeUrl = (desired: string) => {
            if (routingType === 'hash') {
                if (location.pathname !== desired) {
                    navigate(desired, { replace: true });
                }
            } else {
                if (window.location.pathname !== desired) {
                    window.history.replaceState(null, '', desired);
                }
            }
        };

        if (openedEvent) {
            hasOpenedRef.current = true;
            writeUrl('/event/' + openedEvent.slug);
        } else if (hasOpenedRef.current) {
            writeUrl('/');
        }
    }, [openedEvent, navigate, location.pathname, routingType]);


    const openEvent = useCallback(function openEvent(id: string) {
        const event = repositoryRef.current.get(id);
        if (!event) {
            console.log('failed to open');
            return;
        }
        selectEvent(id, event.x, event.y);
        setOpenedEvent(event);
    }, [selectEvent]);

    const deselectEvent = useCallback(function deselectEvent() {
        setSelectedEvent(null);
        setOpenedEvent(null);
    }, []);

    const getEvent = useCallback(function getEvent(id: string) {
        return repositoryRef.current.get(id) || undefined;
    }, []);

    const updateEvents = useCallback(function updateEvents() {
        const events = repositoryRef.current.events;
        // The repo's `events` getter rebuilds fresh arrays on every call, so a
        // naive setState would flip the reference even when nothing changed —
        // which re-runs the canvas cluster-compute effect and produces a
        // visible "jump" mid-drag (observed: 3 cluster regroups per moveend
        // because loadQuery calls updateEvents once before await and once
        // after). Bail out when the contents match by item identity.
        setAllFilteredEvents((prev) => shallowArrayEqual(prev, events.filtered) ? prev : events.filtered);
        setEvents((prev) => shallowArrayEqual(prev, events.sliced) ? prev : events.sliced);
        // Pull the latest cluster snapshot from the v3 repo. Empty array when
        // the response was unclustered (env off or zoom > threshold). The
        // repo has no `.clusters` getter today, so it's typically undefined —
        // normalize to [] so shallowArrayEqual doesn't crash.
        const clusters = repositoryRef.current.clusters ?? [];
        setClusters((prev) => shallowArrayEqual(prev, clusters) ? prev : clusters);
        // The repo hands back a fresh bbox object on each full-viewport fetch
        // (and null on non-armed/empty fetches), so reference identity is the
        // right signal: re-applying the same tag yields a new object and re-fits,
        // while pans that reset it to null read as "no fit".
        const bbox = repositoryRef.current.globalBoundingBox ?? null;
        setGlobalBoundingBox((prev) => prev === bbox ? prev : bbox);
        console.log(events);
    }, []);

    const ensureLoad = useCallback(async function ensureLoad(id: string) {
        await repositoryRef.current.load(id, true);
        updateEvents();
        return repositoryRef.current.get(id) || undefined;
    }, [updateEvents]);

    useEffect(() => {
        // Push the latest zoom to the v3 repo so the next /api/v3/search request
        // carries it. Defaults to 10 if the upstream query doesn't have one yet.
        repositoryRef.current.zoom = query.zoom ?? 10;
        repositoryRef.current.query.set({
            ...query,
            amount: resolveAmount(query, isMobile),
        });
        loadQuery();
    }, [query, isMobile]);

    const loadQuery = useCallback(
        async function loadQuery() {
            console.log('[loadQuery] Starting, loadFromQuery:', loadFromQuery);
            if (loadFromQuery) {
                setCallcount((c) => c + 1);
                updateEvents();
                const quadrants = await repositoryRef.current.loadQuery();
                console.log('[loadQuery] Got quadrants:', quadrants.length);
                for (const q of quadrants) {
                    setCallcount((c) => c + 1);
                    console.log('[loadQuery] loading quadrant', q);
                    repositoryRef.current.queryQuadrant(q).then(() => {
                        console.log('[loadQuery] quadrant done, updating events');
                        updateEvents();
                        setCallcount((c) => c - 1);
                    });
                }
                updateEvents();
                setCallcount((c) => c - 1);
            } else {
                console.log('[loadQuery] Not loading from query, just updating events');
                updateEvents();
            }
        },
        [loadFromQuery]
    );

    const loadAll = useCallback(async function loadAll(ids: string[]) {
        console.log('loadAll');
        setCallcount((c) => c + 1);
        await repositoryRef.current.loadAll(ids);
        updateEvents();
        setCallcount((c) => c - 1);
    }, []);

    const refreshEvents = useCallback(async function refreshEvents() {
        console.log('[refreshEvents] Starting - clearing cache and reloading');
        // Clear the repository cache to force fresh data
        repositoryRef.current.clear();

        // Directly execute the query logic instead of calling loadQuery
        // to avoid stale closure issues
        setCallcount((c) => c + 1);
        const quadrants = await repositoryRef.current.loadQuery();
        console.log('[refreshEvents] Got quadrants:', quadrants.length);

        // Wait for all quadrants to load
        const quadrantPromises = quadrants.map(async (q) => {
            console.log('[refreshEvents] Loading quadrant', q);
            await repositoryRef.current.queryQuadrant(q);
        });

        await Promise.all(quadrantPromises);

        // Update events after all quadrants are loaded
        const events = repositoryRef.current.events;
        setAllFilteredEvents(events.filtered);
        setEvents(events.sliced);
        setCallcount((c) => c - 1);

        console.log('[refreshEvents] Done - events:', events.sliced.length);
    }, []);

    useEffect(() => {
        if (selectRandomEvent && !selectedEvent && events.length > 0) {
            const random = Math.floor(Math.random() * events.length);
            selectEvent(events[random].id);
        }
    }, [events]);

    // Auto-select (highlight + center) a pre-selected event on load. Sources:
    //  - `selectedBalloon` option (e.g. the mini-map highlighting the current event)
    //  - `initialSelectedEventId` (navigating back from EventPage via ?select=ID|SLUG)
    // Uses ensureLoad so the event is fetched directly regardless of current map viewport.
    const preselectEventId = selectedBalloon ?? initialSelectedEventId;
    useEffect(() => {
        if (initialSelectDoneRef.current || !preselectEventId) return;
        initialSelectDoneRef.current = true;
        ensureLoad(preselectEventId).then((target) => {
            if (!target) return;
            selectEvent(target.id);
        });
    }, [preselectEventId]);

    // Listen for balloon event create/update notifications (fired by simple-creator)
    useEffect(() => {
        return BalloonEventNotifier.listen(async (payload) => {
            console.log('[EventDataProvider] Received balloon event notification:', payload);
            await refreshEvents();
            // Load the specific event and open it
            const loaded = await ensureLoad(payload.slug || payload.id);
            if (loaded) {
                openEvent(payload.slug || payload.id);
            }
        });
    }, [refreshEvents, ensureLoad, openEvent]);

    return (
        <EventDataContext.Provider
            value={{
                events: loadFromQuery ? events : injected,
                clusters,
                globalBoundingBox,
                selectEvent,
                deselectEvent,
                selectedEvent,
                allFilteredEvents,
                ensureLoad,
                loading: callcount > 0,
                getEvent,
                eventMap: repositoryRef.current.eventMap,
                hoveredEventId,
                setHoveredEventId,
                openedEvent,
                setOpenedEvent,
                refreshEvents,
                openEvent
            }}
        >
            {children}
        </EventDataContext.Provider>
    );
};

export function useBalloonEventsData(): EventDataInfo {
    const context = useContext(EventDataContext);
    if (!context) {
        throw new Error(
            'useBalloonEvents must be used within a BalloonEventsProvider'
        );
    }
    return context;
}

/**
 * Safe version of useBalloonEventsData that returns null if not in provider
 * Use this when the component might be rendered outside of EventDataProvider
 */
export function useBalloonEventsDataSafe(): EventDataInfo | null {
    return useContext(EventDataContext) ?? null;
}
